import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import CountryFlag from './CountryFlag';
import { useAnchoredDropdown } from './selectStyles';

// Per-row translated country name. Renders inside dropdown items + selected display.
const CountryName = ({ country }) => {
    const label = useMasterLabel('masterCountry', country?.countryId, 'countryName', country?.countryName || '');
    return <>{label}</>;
};

const CountrySelect = ({
    value,
    onChange,
    countries = [],
    placeholder,
    disabled = false,
    error = false,
    className = '',
    size = 'default', // 'default' | 'small' (for filter dropdowns)
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const panelOpen = isOpen && !disabled;
    const { anchorRef, panelRef, panelStyle } = useAnchoredDropdown(panelOpen);

    const selectedCountry = countries?.find(c => c.countryId === value) || null;

    const filteredCountries = countries?.filter(c =>
        c.countryName?.toLowerCase().includes(search.toLowerCase())
    ) || [];

    // Close on outside click. The panel is portaled to document.body, so it is not
    // a descendant of containerRef — without the panelRef test, mousedown inside
    // the panel (an option, or the search box) would close it before the click.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setIsOpen(false);
            setSearch('');
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, panelRef]);

    // Focus search when opened
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleSelect = (countryId) => {
        onChange(countryId);
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setIsOpen(false);
        setSearch('');
    };

    const isSmall = size === 'small';

    return (
        <div ref={containerRef} className={`relative ${className}`} data-testid="country-select-container">
            {/* Trigger Button */}
            <button
                ref={anchorRef}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full ${isSmall ? 'px-3 py-2' : 'px-4 py-3'} rounded-xl border text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    error
                        ? 'border-red-500 bg-red-50'
                        : isOpen
                            ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/20 bg-white'
                            : 'border-gray-300 hover:border-[#4ABDBD] bg-white'
                } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
                data-testid="country-select-trigger-button"
            >
                <span className={`truncate flex items-center gap-2 ${!selectedCountry ? 'text-gray-400' : 'text-gray-800'}`} data-testid="country-select-selected-text">
                    {selectedCountry && (
                        <CountryFlag
                            countryName={selectedCountry.countryName}
                            alt={selectedCountry.countryName}
                            className="w-5 h-3.5 object-cover rounded-sm shrink-0"
                        />
                    )}
                    {selectedCountry ? <CountryName country={selectedCountry} /> : (placeholder || t('common.select_country', 'Select Country'))}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {value && !disabled && (
                        <span
                            onClick={handleClear}
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                            data-testid="country-select-clear-button"
                        >
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" data-testid="country-select-clear-icon" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-testid="country-select-chevron-icon" />
                </div>
            </button>

            {/* Dropdown — portaled to document.body at DROPDOWN_Z_INDEX. This is the
                Add Doctor country list from the bug report: as an absolute z-50
                panel it painted under the z-[9999] modal body and read as the
                country field "merging" into the fields below it. */}
            {panelOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={panelRef}
                    style={panelStyle}
                    className="flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
                    data-testid="country-select-dropdown"
                >
                    {/* Search Bar — never shrinks, so a squeezed panel takes the
                        height out of the list below, which can scroll. */}
                    <div className="p-2 border-b border-gray-100 flex-shrink-0" data-testid="country-select-search-section">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="country-select-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('common.search_country', 'Search country...')}
                                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                data-testid="country-select-search-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                                    data-testid="country-select-search-clear-button"
                                >
                                    <X className="w-3.5 h-3.5 text-gray-400" data-testid="country-select-search-clear-icon" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-48 min-h-0 overflow-y-auto custom-scrollbar" data-testid="country-select-options-list">
                        {filteredCountries.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-400" data-testid="country-select-no-results-text">
                                {t('common.no_countries_found', 'No countries found')}
                            </div>
                        ) : (
                            filteredCountries.map((c) => {
                                const isSelected = value === c.countryId;
                                return (
                                    <button
                                        key={c.countryId}
                                        type="button"
                                        onClick={() => handleSelect(c.countryId)}
                                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-all duration-150 ${
                                            isSelected
                                                ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                        data-testid={`country-select-option-${c.countryId}`}
                                    >
                                        <CountryFlag
                                            countryName={c.countryName}
                                            alt={c.countryName}
                                            className="w-5 h-3.5 object-cover rounded-sm shrink-0"
                                        />
                                        <span className="truncate flex-1 text-left"><CountryName country={c} /></span>
                                        {isSelected && (
                                            <Check className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" data-testid={`country-select-option-check-icon-${c.countryId}`} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CountrySelect;
